/**
 * Fiches Q/R précises pour Nimba (assistant Conakry en Poche).
 * Matching par score de mots-clés → réponse ciblée (prioritaire sur le tirage aléatoire).
 */
export const assistantFacts = {
  fr: [
    {
      id: 'aeroport',
      category: 'transport',
      keywords: ['aéroport', 'aeroport', 'gbessia', 'avion', 'vol', 'arrivée', 'arrivée', 'débarquement'],
      answer:
        '**Aéroport international de Conakry (Gbessia – CKY)**\n\n• Situé à environ 30–45 min du centre (Kaloum), selon le trafic.\n• Taxis et VTC disponibles à la sortie ; **négociez le prix avant de partir** (souvent 80 000–150 000 GNF selon l’heure et la destination).\n• Dans l’app : ouvrez **Commander un taxi** ou cherchez les entreprises « transport ».\n• Préparez de la monnaie GNF ou un mobile money (Orange Money / MTN MoMo) à l’arrivée.'
    },
    {
      id: 'taxi-prix',
      category: 'transport',
      keywords: ['prix taxi', 'tarif taxi', 'combien taxi', 'coût taxi', 'cout taxi', 'taxi prix', 'prix d\'un taxi', 'combien coûte', 'combien coute'],
      answer:
        '**Tarifs taxi à Conakry (indicatif)**\n\n• Court trajet urbain : ~5 000–15 000 GNF\n• Trajet moyen (ex. Dixinn ↔ Kaloum) : ~15 000–40 000 GNF\n• Long trajet / aéroport : souvent 80 000–150 000 GNF\n\nToujours fixer le prix avant de monter. Les motos-taxis (zémidjan) sont moins chères pour les courts trajets (~2 000–8 000 GNF).\n\nDans Conakry en Poche : ouvrez **Commander un taxi** pour trouver un chauffeur validé.'
    },
    {
      id: 'allo-taxi',
      category: 'application',
      keywords: ['allo taxi', 'allotaxi', 'commander taxi', 'appeler taxi', 'réserver taxi', 'reserver taxi'],
      answer:
        '**Commander un taxi dans Conakry en Poche**\n\n1. Ouvrez le menu → **Commander un taxi**\n2. Choisissez un chauffeur validé\n3. Confirmez départ et destination, puis suivez la course\n\nVous pouvez aussi appeler ou envoyer un SMS si le réseau est faible. Les chauffeurs s’inscrivent via « Inscrire mon taxi » (validation par l’équipe).'
    },
    {
      id: 'urgences',
      category: 'sante',
      keywords: ['urgence', 'urgences', 'numéro', 'numero', '117', '118', '119', 'samu', 'pompier', 'police'],
      answer:
        '**Numéros d’urgence en Guinée**\n\n• **117** — Police\n• **118** — Pompiers\n• **119** — SAMU / urgences médicales\n\nHôpital de référence à Conakry : **Ignace Deen** (Kaloum). Pharmacies nombreuses ; certaines tournent la nuit.\n\nDans l’app : catégories **Hôpitaux** et **Pharmacies** + carte / recherche.'
    },
    {
      id: 'mobile-money',
      category: 'banques',
      keywords: ['orange money', 'mtn money', 'momo', 'mobile money', 'transfert', 'envoyer argent', 'wave'],
      answer:
        '**Mobile money à Conakry**\n\n• **Orange Money** et **MTN MoMo** sont très utilisés pour payer, envoyer et retirer de l’argent.\n• Points de service : kiosques Orange/MTN, commerces partenaires.\n• Pratique au quotidien (taxi, marché, factures) quand les espèces manquent.\n\nLes banques (BICIGUI, Ecobank, UBA, Société Générale, etc.) et DAB restent utiles pour retraits carte Visa/Mastercard.'
    },
    {
      id: 'change',
      category: 'economie',
      keywords: ['change', 'changer devises', 'dollar', 'euro', 'taux', 'bureau de change', 'devises'],
      answer:
        '**Change de devises à Conakry**\n\n• Bureaux de change et banques à **Kaloum** et zones commerciales.\n• Comparez les taux ; gardez vos reçus.\n• La monnaie locale est le **franc guinéen (GNF)** — les petites coupures aident pour taxis et marchés.\n\nÉvitez de changer de grosses sommes dans la rue. Préférez un établissement identifié.'
    },
    {
      id: 'inscription-entreprise',
      category: 'application',
      keywords: ['inscrire entreprise', 'inscription entreprise', 'ajouter entreprise', 'enregistrer entreprise', 'tarif inscription', 'inscrire mon entreprise', 'mon entreprise'],
      answer:
        '**Inscrire une entreprise sur Conakry en Poche**\n\n1. Menu → **S’inscrire** / Inscrire mon entreprise (connexion requise)\n2. Remplissez le formulaire (nom, catégorie, adresse, contacts)\n3. Validation sous ~48 h\n\n**Inscription à vie** : visibilité permanente, sans commission sur les ventes.\n\nSupport : admin@conakryenpoche.com · +224 663 07 82 68'
    },
    {
      id: 'agent-commercial',
      category: 'application',
      keywords: [
        'agent commercial',
        'devenir agent',
        'commission',
        '2%',
        '2 %',
        'propre patron',
        'être son propre patron',
        'etre son propre patron',
        'opportunité',
        'opportunite',
        'gagner de l’argent',
        'gagner de l argent'
      ],
      answer:
        '**Opportunités Conakry en Poche**\n\n• **Agent commercial** — Écosystème / Accueil → **Devenir agent**. Partagez votre lien `/a/CEP-A-…`, gagnez **2 %** sur les frais plateforme (entreprise, location, activité). Retraits **Mobile Money**. Contrat : `/terms-agent`.\n• **Être son propre patron** — Inscrivez votre **entreprise**, votre **agence de location** ou publiez une **activité**. Pas de commission sur vos ventes.\n\nTout se fait en ligne, sans rendez-vous avec la gestion.'
    },
    {
      id: 'quartiers-guide',
      category: 'quartiers',
      keywords: ['quartier', 'kaloum', 'dixinn', 'ratoma', 'matam', 'matoto', 'où habiter', 'ou habiter', 'zone'],
      answer:
        '**Communes / quartiers utiles à Conakry**\n\n• **Kaloum** — centre administratif & affaires (embouteillages)\n• **Dixinn** — résidentiel, hôtels, université\n• **Ratoma** — mixte, plus abordable\n• **Matam** — commerces & marchés\n• **Matoto** — résidentiel, plus calme\n• **Hamdallaye** — marchés, vie de quartier\n• **Taouyah / Rogbané** — côté mer / plages\n\nDites-moi ce que vous cherchez (logement, resto, admin) pour affiner.'
    },
    {
      id: 'plages',
      category: 'loisirs',
      keywords: ['plage', 'plages', 'baignade', 'rogbané', 'rogbane', 'taouyah', 'mer'],
      answer:
        '**Plages près de Conakry**\n\n• **Rogbané** et **Taouyah** sont parmi les plus citées pour se détendre le week-end.\n• Préférez la **saison sèche** (nov.–avr.).\n• Surveillez vos affaires ; évitez de vous baigner seul dans des zones isolées.\n\nPour une sortie mer : l’**archipel des Îles de Loos** se rejoint en bateau depuis le port.'
    },
    {
      id: 'iles-loos',
      category: 'tourisme',
      keywords: ['loos', 'île', 'ile', 'îles de loos', 'iles de loos', 'excursion', 'bateau'],
      answer:
        '**Îles de Loos**\n\nExcursion classique depuis Conakry : départ en bateau depuis le port / points d’embarquement locaux.\n\n• Idéal pour une journée (plages, nature, déjeuner)\n• Négociez le prix de la traversée à l’avance\n• Emportez crème solaire, eau, espèces\n\nMeilleure période : saison sèche (novembre à avril).'
    },
    {
      id: 'visa',
      category: 'administratif',
      keywords: ['visa', 'e-visa', 'evisa', 'entrée', 'entree', 'séjour', 'sejour', 'passeport'],
      answer:
        '**Visa & séjour en Guinée**\n\n• Renseignez-vous auprès de l’ambassade / consulat de Guinée dans votre pays.\n• Un **e-Visa** peut être disponible via le portail officiel PAF : https://www.paf.gov.gn/visa\n• Passeport valide (souvent 6 mois minimum) + certificats de vaccination selon les règles en vigueur.\n\nLes délais et types de visa (tourisme, séjour) évoluent : vérifiez toujours les sources officielles avant de voyager.'
    },
    {
      id: 'paludisme',
      category: 'sante',
      keywords: ['paludisme', 'malaria', 'moustique', 'vaccin', 'vaccination', 'fièvre', 'fievre'],
      answer:
        '**Santé & paludisme**\n\n• La prévention du **paludisme** (moustiquaire, répulsif, traitement prophylactique selon avis médical) est essentielle.\n• Consultez un médecin voyage **avant** le départ pour les vaccins recommandés.\n• Buvez de l’**eau potable** (bouteille scellée ou filtrée) ; prudence avec glaçons et crudités.\n\nUrgences : **119**. Hôpitaux / pharmacies : catégories dans l’application.'
    },
    {
      id: 'electricite',
      category: 'services',
      keywords: ['électricité', 'electricite', 'edg', 'coupure', 'courant', 'groupe électrogène', 'groupe electrogene', 'blackout'],
      answer:
        '**Électricité à Conakry**\n\nDes coupures peuvent survenir. Beaucoup d’hôtels, entreprises et foyers utilisent un **groupe électrogène** ou des solutions solaires.\n\nConseils :\n• Chargez téléphone / powerbank\n• Prévoyez une lampe\n• Demandez à votre hébergement le rythme des coupures'
    },
    {
      id: 'trafic',
      category: 'transport',
      keywords: ['trafic', 'embouteillage', 'bouchon', 'circulation', 'route', 'encombrement'],
      answer:
        '**Trafic à Conakry**\n\nLes embouteillages sont fréquents, surtout à **Kaloum** et aux heures de pointe.\n\nAnticipez vos déplacements, privilégiez Commander un taxi ou une location de voiture, et négociez les courses en conséquence.'
    },
    {
      id: 'restaurants-guide',
      category: 'restaurants',
      keywords: ['restaurant', 'manger', 'où manger', 'ou manger', 'maquis', 'yassa', 'riz gras'],
      answer:
        '**Où manger à Conakry ?**\n\n• Cuisine locale : **riz gras**, **poulet yassa**, **sauce arachide**, poisson grillé, fouti…\n• Formats : maquis (petits restos), restaurants, hôtels, cuisine de rue aux marchés.\n• Zones animées : Dixinn, Kaloum, Matam, Ratoma.\n\nDans l’app : catégorie **Restaurants** ou barre de recherche. Filtrez par zone si vous avez une préférence.'
    },
    {
      id: 'hotels-guide',
      category: 'hotels',
      keywords: ['hôtel', 'hotel', 'hébergement', 'hebergement', 'dormir', 'où dormir', 'ou dormir'],
      answer:
        '**Hébergement à Conakry**\n\nDes établissements pour tous budgets. Zones souvent citées : **Dixinn**, **Kaloum**, **Taouyah**.\n\nDans l’app : catégorie **Hôtels** → **Réserver** (arrivée, départ, chambres). Suivez vos séjours dans **Mes réservations**.\n\nComparez adresse / téléphone / notes, puis navigation GPS. Indiquez budget et quartier pour une suggestion plus précise.'
    },
    {
      id: 'climat',
      category: 'climat',
      keywords: ['climat', 'météo', 'meteo', 'pluie', 'saison', 'chaud', 'température', 'temperature'],
      answer:
        '**Climat à Conakry**\n\n• **Saison sèche** : nov.–avr. (souvent la plus agréable pour visiter)\n• **Saison des pluies** : mai–oct. (fortes averses, humidité)\n• Températures tropicales ~25–32 °C\n\nEmportez vêtements légers, protection solaire, et un parapluie en saison des pluies.'
    },
    {
      id: 'sim-internet',
      category: 'communication',
      keywords: ['sim', 'internet', 'wifi', 'wi-fi', 'orange', 'mtn', 'cellcom', 'forfait', '4g'],
      answer:
        '**Téléphonie & internet**\n\n• Opérateurs : **Orange**, **MTN**, Cellcom — cartes SIM en boutique / aéroport.\n• Forfaits data 3G/4G courants ; WhatsApp très utilisé.\n• Wi‑Fi : hôtels, cafés, certains restos.\n\nAcheter une SIM locale dès l’arrivée évite les frais d’itinérance.'
    },
    {
      id: 'mot-de-passe',
      category: 'application',
      keywords: ['mot de passe', 'password', 'oublié', 'oublie', 'réinitialiser', 'reinitialiser', 'connexion'],
      answer:
        '**Problème de connexion / mot de passe**\n\n1. Page Connexion → **Mot de passe oublié**\n2. Entrez votre email → lien de réinitialisation\n3. Vérifiez aussi les spams\n\nToujours bloqué ? Contactez **admin@conakryenpoche.com** ou WhatsApp **+224 663 07 82 68**.'
    },
    {
      id: 'location-voiture',
      category: 'application',
      keywords: ['location', 'louer', 'voiture', 'location voiture', 'rental', 'agence location', 'loueur'],
      answer:
        '**Location de voiture**\n\nDans l’app : **Louer une voiture** (accueil) ou menu → Location de voiture.\n\nChoisissez un véhicule, remplissez le formulaire (dates, permis, prise en charge). L’agence confirme via son tableau de bord. Les loueurs s’inscrivent via **Inscrire mon agence de location**.'
    },
    {
      id: 'contact-support',
      category: 'application',
      keywords: ['contact', 'support', 'aide', 'assistance', 'email', 'téléphone', 'telephone', 'whatsapp', 'facebook'],
      answer:
        '**Contacter Conakry en Poche**\n\n• Email : **admin@conakryenpoche.com**\n• WhatsApp : **+224 663 07 82 68** (uniquement)\n• Facebook : **facebook.com/Conakryenpoche**\n• Formulaire : page Contact de l’application\n\nJe peux aussi vous mettre en relation via WhatsApp si votre question dépasse ma base de connaissances.'
    },
    {
      id: 'marches',
      category: 'shopping',
      keywords: ['marché', 'marche', 'madina', 'niger', 'shopping', 'acheter', 'négocier', 'negoci'],
      answer:
        '**Marchés & shopping**\n\n• **Marché Madina**, **Marché du Niger** : très fréquentés pour produits locaux, vêtements, artisanat.\n• Les prix se **négocient** souvent.\n• Préférez les jours de semaine pour moins d’affluence.\n\nCentres / boutiques modernes existent aussi ; pour l’artisanat, les marchés restent incontournables.'
    },
    {
      id: 'independance',
      category: 'culture',
      keywords: ['indépendance', 'independance', '2 octobre', 'fête nationale', 'fete nationale'],
      answer:
        '**Fête de l’Indépendance**\n\nCélébrée le **2 octobre** : parades, concerts et festivités à Conakry et dans le pays.\n\nAutres temps forts : 1er mai, fêtes religieuses (Ramadan, Tabaski — dates variables), Noël / Nouvel An, festivals culturels.\n\nConsultez aussi **Événements** dans l’application.'
    },
    {
      id: 'eau',
      category: 'services',
      keywords: ['eau', 'boire', 'potable', 'bouteille'],
      answer:
        '**Eau potable**\n\nPrivilégiez l’eau en **bouteille scellée** ou filtrée. Évitez l’eau du robinet pour boire si vous n’êtes pas habitué.\n\nAttention aux glaçons et crudités lavées à l’eau non sûre dans certains établissements.'
    },
    {
      id: 'vivre-guinee',
      category: 'logement',
      keywords: ['vivre en guinée', 'vivre en guinee', 's’installer', 'sinstaller', 'expat', 'emménager', 'emmenager'],
      answer:
        '**S’installer / vivre en Guinée**\n\nConsultez la page **Vivre en Guinée** dans l’app : culture, langues, santé, logement, démarches, numéros utiles, liens officiels (gouvernement, e-Visa, e-Tax, APIP).\n\nPour le logement : comparez Kaloum (cher), Dixinn, Ratoma, Matoto ; visitez plusieurs biens et vérifiez eau / électricité.'
    },
    {
      id: 'ecosystem-overview',
      category: 'application',
      keywords: ['écosystème', 'ecosysteme', 'portefeuille', 'wallet', 'services numériques', 'services numeriques', 'cep wallet'],
      answer:
        '**Écosystème Conakry en Poche**\n\nDans l’app → **Écosystème** :\n• **Portefeuille** — solde GNF, recharge Orange Money / MTN MoMo\n• **Encaisser (QR)** — paiement commerçant\n• **RDV médecin** — consultation + paiement wallet\n• **Cinéma / Tourisme** — billets numériques\n\nLa diaspora peut aussi **recharger une carte à distance** (lien partageable + carte bancaire Stripe).'
    },
    {
      id: 'wallet-topup',
      category: 'application',
      keywords: ['recharger portefeuille', 'recharge wallet', 'top up', 'topup', 'créditer', 'crediter', 'solde', 'orange money portefeuille', 'mtn portefeuille'],
      answer:
        '**Recharger le portefeuille**\n\n1. Écosystème → **Portefeuille**\n2. Choisissez un montant et **Orange Money** ou **MTN MoMo**\n3. Validez sur votre téléphone\n\nSans clés marchandes configurées, un **mode démo** permet de confirmer manuellement. Le crédit apparaît ensuite sur votre carte CEP.'
    },
    {
      id: 'diaspora-recharge',
      category: 'application',
      keywords: ['diaspora', 'recharge à distance', 'recharge a distance', 'lien diaspora', 'étranger', 'etranger', 'stripe', 'carte internationale', 'carte bancaire', 'reçu', 'recu', 'pdf'],
      answer:
        '**Recharge diaspora**\n\n1. Le titulaire ouvre **Portefeuille** → **Partager le lien diaspora**\n2. Le payeur ouvre `/recharger/CEP-…` (sans se connecter)\n3. Il choisit Mobile Money ou **carte internationale (Stripe)**\n4. Le bénéficiaire est crédité ; un **reçu PDF / email** est disponible\n\nLe nom complet du bénéficiaire s’affiche sur la page de paiement.'
    },
    {
      id: 'merchant-qr',
      category: 'application',
      keywords: ['encaisser', 'qr commerçant', 'qr commercant', 'paiement qr', 'collecter', 'caisse', 'notification sms', 'notification email', 'payer commerçant', 'payer commercant'],
      answer:
        '**Encaissement QR commerçant**\n\n• Commerçant : Écosystème → **Encaisser (QR)** (ou tableau de bord business)\n• Affichez le QR (montant optionnel)\n• Client : scanne → paie avec son **portefeuille CEP**\n• Vous êtes notifié par **email et SMS**\n\nLe solde est crédité immédiatement sur le portefeuille commerçant.'
    },
    {
      id: 'hotel-booking',
      category: 'hotels',
      keywords: ['réserver hôtel', 'reserver hotel', 'réservation hôtel', 'reservation hotel', 'check-in', 'check in', 'arrivée hôtel', 'arrivee hotel', 'chambre'],
      answer:
        '**Réserver un hôtel**\n\n1. Catégorie **Hôtels**\n2. Ouvrez un établissement → **Réserver**\n3. Indiquez **arrivée**, **départ** et **nombre de chambres**\n4. Confirmez (connexion requise)\n\nSuivez ou annulez depuis **Mes réservations** (`/bookings`). L’hôtel reçoit une notification.'
    },
    {
      id: 'health-rdv',
      category: 'sante',
      keywords: ['rendez-vous médecin', 'rdv médecin', 'rdv medecin', 'consultation', 'prendre rdv', 'santé écosystème', 'sante ecosysteme'],
      answer:
        '**RDV médecin dans l’écosystème**\n\nÉcosystème → **Santé** : choisissez un praticien / créneau, confirmez et **payez avec le portefeuille** Conakry en Poche.\n\nPensez à recharger votre solde avant si besoin. Les urgences restent au **119**.'
    },
    {
      id: 'tickets-cinema-tourisme',
      category: 'loisirs',
      keywords: ['billet cinéma', 'billet cinema', 'cinéma', 'cinema', 'ticket tourisme', 'site touristique', 'entrée site', 'entree site'],
      answer:
        '**Billets cinéma & tourisme**\n\n• Écosystème → **Cinéma** : séances et billet numérique\n• Écosystème → **Tourisme** : entrées sites\n\nPaiement via le **portefeuille CEP**. Le billet (QR) est visible dans le portefeuille ; partage / impression possibles.'
    },
  ],
  en: [
    {
      id: 'aeroport',
      category: 'transport',
      keywords: ['airport', 'gbessia', 'flight', 'plane', 'arrival', 'landing', 'cky'],
      answer:
        '**Conakry International Airport (Gbessia – CKY)**\n\n• About 30–45 minutes from downtown Kaloum, depending on traffic.\n• Taxis available outside; **agree on the fare before leaving** (often 80,000–150,000 GNF).\n• In the app: open **Book a taxi** or search transport businesses.\n• Bring GNF cash or mobile money (Orange Money / MTN MoMo).'
    },
    {
      id: 'taxi-prix',
      category: 'transport',
      keywords: ['taxi price', 'taxi fare', 'how much taxi', 'taxi cost', 'fare'],
      answer:
        '**Typical taxi fares in Conakry**\n\n• Short trip: ~5,000–15,000 GNF\n• Medium trip: ~15,000–40,000 GNF\n• Airport / long trip: often 80,000–150,000 GNF\n\nAlways agree on the price before boarding. Moto-taxis (zemidjan) are cheaper for short hops (~2,000–8,000 GNF).\n\nUse **Book a taxi** in Conakry en Poche to find validated drivers.'
    },
    {
      id: 'allo-taxi',
      category: 'application',
      keywords: ['allo taxi', 'allotaxi', 'call taxi', 'book taxi', 'order taxi'],
      answer:
        '**Book a taxi in Conakry en Poche**\n\n1. Open the menu → **Book a taxi**\n2. Choose a verified driver\n3. Confirm pickup and destination, then follow the ride\n\nYou can also call or send an SMS if the network is weak. Drivers register via “Register my taxi” (team validation).'
    },
    {
      id: 'urgences',
      category: 'sante',
      keywords: ['emergency', 'emergencies', '117', '118', '119', 'police', 'ambulance', 'fire'],
      answer:
        '**Emergency numbers in Guinea**\n\n• **117** — Police\n• **118** — Firefighters\n• **119** — Medical emergencies / SAMU\n\nMain reference hospital in Conakry: **Ignace Deen** (Kaloum).\n\nIn the app: **Hospitals** and **Pharmacies** categories.'
    },
    {
      id: 'mobile-money',
      category: 'banques',
      keywords: ['orange money', 'mtn money', 'momo', 'mobile money', 'transfer', 'send money'],
      answer:
        '**Mobile money in Conakry**\n\n• **Orange Money** and **MTN MoMo** are widely used to pay, send and withdraw cash.\n• Find agents in Orange/MTN kiosks and partner shops.\n• Very handy for taxis, markets and bills.\n\nBanks (BICIGUI, Ecobank, UBA, Société Générale, etc.) and ATMs remain useful for Visa/Mastercard withdrawals.'
    },
    {
      id: 'change',
      category: 'economie',
      keywords: ['exchange', 'currency', 'dollar', 'euro', 'rate', 'forex', 'change money'],
      answer:
        '**Currency exchange in Conakry**\n\n• Exchange offices and banks in **Kaloum** and commercial areas.\n• Compare rates and keep receipts.\n• Local currency: **Guinean franc (GNF)** — small notes help for taxis and markets.\n\nAvoid exchanging large amounts on the street.'
    },
    {
      id: 'inscription-entreprise',
      category: 'application',
      keywords: ['register business', 'add business', 'business registration', 'listing fee'],
      answer:
        '**Register a business on Conakry en Poche**\n\n1. Menu → **Register my business** (login required)\n2. Fill in the form (name, category, address, contacts)\n3. Review within ~48 hours\n\n**Lifetime listing**: permanent visibility, no sales commission.\n\nSupport: admin@conakryenpoche.com · +224 663 07 82 68'
    },
    {
      id: 'quartiers-guide',
      category: 'quartiers',
      keywords: ['neighborhood', 'neighbourhood', 'kaloum', 'dixinn', 'ratoma', 'matam', 'matoto', 'where to stay', 'area'],
      answer:
        '**Useful Conakry areas**\n\n• **Kaloum** — admin & business center (heavy traffic)\n• **Dixinn** — residential, hotels, university\n• **Ratoma** — mixed, more affordable\n• **Matam** — markets & commerce\n• **Matoto** — quieter residential\n• **Hamdallaye** — local markets & neighborhood life\n• **Taouyah / Rogbané** — beach side\n\nTell me what you need (housing, food, admin) for a sharper tip.'
    },
    {
      id: 'plages',
      category: 'loisirs',
      keywords: ['beach', 'beaches', 'swim', 'rogbane', 'taouyah', 'sea'],
      answer:
        '**Beaches near Conakry**\n\n• **Rogbané** and **Taouyah** are popular weekend spots.\n• Prefer the **dry season** (Nov–Apr).\n• Watch your belongings; avoid swimming alone in isolated areas.\n\nFor a sea day trip: the **Loos Islands** are reachable by boat from the port.'
    },
    {
      id: 'iles-loos',
      category: 'tourisme',
      keywords: ['loos', 'island', 'islands', 'boat trip', 'excursion'],
      answer:
        '**Loos Islands**\n\nA classic day trip from Conakry by boat from the port / local embarkation points.\n\n• Great for beaches and lunch\n• Agree on the boat price beforehand\n• Bring sunscreen, water, cash\n\nBest period: dry season (November–April).'
    },
    {
      id: 'visa',
      category: 'administratif',
      keywords: ['visa', 'e-visa', 'evisa', 'entry', 'passport', 'stay'],
      answer:
        '**Visa & stay in Guinea**\n\n• Check with the Guinean embassy/consulate in your country.\n• An **e-Visa** may be available via the official PAF portal: https://www.paf.gov.gn/visa\n• Passport often needs 6+ months validity + required vaccinations.\n\nRules change — always verify official sources before travel.'
    },
    {
      id: 'paludisme',
      category: 'sante',
      keywords: ['malaria', 'mosquito', 'vaccine', 'vaccination', 'fever'],
      answer:
        '**Health & malaria**\n\n• **Malaria** prevention (net, repellent, prophylaxis as advised by a doctor) is essential.\n• See a travel clinic **before** departure for recommended vaccines.\n• Drink **bottled or filtered water**; be careful with ice and raw salads.\n\nEmergencies: **119**. Hospitals/pharmacies: app categories.'
    },
    {
      id: 'electricite',
      category: 'services',
      keywords: ['electricity', 'power', 'outage', 'blackout', 'generator', 'edg'],
      answer:
        '**Electricity in Conakry**\n\nOutages can happen. Many hotels and homes use a **generator** or solar backup.\n\nTips: keep your phone charged, carry a power bank, ask your host about outage patterns.\n\nFor road-related incidents, use **Report** in the app.'
    },
    {
      id: 'trafic',
      category: 'transport',
      keywords: ['traffic', 'jam', 'congestion', 'road', 'gridlock'],
      answer:
        '**Traffic in Conakry**\n\nJams are common, especially in **Kaloum** at peak hours.\n\nIn Conakry en Poche:\n• **Traffic map**\n• **Report an issue** (works, accident, jam)\n\nPlan extra time and negotiate taxi fares accordingly.'
    },
    {
      id: 'restaurants-guide',
      category: 'restaurants',
      keywords: ['restaurant', 'eat', 'where to eat', 'food', 'yassa', 'jollof'],
      answer:
        '**Where to eat in Conakry**\n\n• Local dishes: **riz gras**, **chicken yassa**, peanut sauce, grilled fish, fouti…\n• Formats: maquis, restaurants, hotels, market street food.\n• Lively areas: Dixinn, Kaloum, Matam, Ratoma.\n\nIn the app: **Restaurants** category or search bar.'
    },
    {
      id: 'hotels-guide',
      category: 'hotels',
      keywords: ['hotel', 'hotels', 'accommodation', 'lodging', 'where to stay', 'sleep'],
      answer:
        '**Accommodation in Conakry**\n\nOptions for all budgets. Often cited areas: **Dixinn**, **Kaloum**, **Taouyah**.\n\nIn the app: **Hotels** → **Book** (check-in, check-out, rooms). Track stays under **My reservations**.\n\nCompare address/phone/ratings, then GPS. Share budget and area for a sharper suggestion.'
    },
    {
      id: 'climat',
      category: 'climat',
      keywords: ['climate', 'weather', 'rain', 'season', 'hot', 'temperature'],
      answer:
        '**Climate in Conakry**\n\n• **Dry season**: Nov–Apr (often best for visits)\n• **Rainy season**: May–Oct (heavy showers, humidity)\n• Tropical temps ~25–32°C\n\nPack light clothes, sun protection, and an umbrella in the rainy season.'
    },
    {
      id: 'sim-internet',
      category: 'communication',
      keywords: ['sim', 'internet', 'wifi', 'wi-fi', 'orange', 'mtn', 'cellcom', 'data', '4g'],
      answer:
        '**Phone & internet**\n\n• Operators: **Orange**, **MTN**, Cellcom — SIM cards in shops / airport.\n• Data packs common; WhatsApp is widely used.\n• Wi‑Fi in hotels, cafés, some restaurants.\n\nA local SIM on arrival usually beats roaming costs.'
    },
    {
      id: 'mot-de-passe',
      category: 'application',
      keywords: ['password', 'forgot', 'reset', 'login', 'sign in'],
      answer:
        '**Login / password issues**\n\n1. Login page → **Forgot password**\n2. Enter your email → reset link\n3. Check spam too\n\nStill stuck? Contact **admin@conakryenpoche.com** or WhatsApp **+224 663 07 82 68**.'
    },
    {
      id: 'location-voiture',
      category: 'application',
      keywords: ['rental', 'rent', 'car', 'car rental', 'hire car', 'vehicle rental'],
      answer:
        '**Car rental**\n\nIn the app: **Rent a car** (home) or menu → Car rental.\n\nPick a vehicle, fill the form (dates, license, pickup). The agency confirms from its dashboard. Operators register via **Register my rental agency**.'
    },
    {
      id: 'contact-support',
      category: 'application',
      keywords: ['contact', 'support', 'help', 'email', 'phone', 'whatsapp', 'facebook'],
      answer:
        '**Contact Conakry en Poche**\n\n• Email: **admin@conakryenpoche.com**\n• WhatsApp: **+224 663 07 82 68** (only)\n• Facebook: **facebook.com/Conakryenpoche**\n• Form: Contact page in the app\n\nI can also offer WhatsApp handoff if your question is beyond my knowledge base.'
    },
    {
      id: 'marches',
      category: 'shopping',
      keywords: ['market', 'madina', 'niger', 'shopping', 'buy', 'bargain', 'negotiate'],
      answer:
        '**Markets & shopping**\n\n• **Madina Market**, **Niger Market**: busy for local goods, clothes, crafts.\n• Prices are often **negotiable**.\n• Weekdays are usually less crowded.\n\nModern shops exist too; for crafts, traditional markets remain key.'
    },
    {
      id: 'independance',
      category: 'culture',
      keywords: ['independence', 'october 2', 'national day'],
      answer:
        '**Independence Day**\n\nCelebrated on **October 2**: parades, concerts and festivities.\n\nOther highlights: May 1, religious holidays (Ramadan, Tabaski — variable dates), Christmas/New Year, cultural festivals.\n\nAlso check **Events** in the app.'
    },
    {
      id: 'eau',
      category: 'services',
      keywords: ['water', 'drink', 'potable', 'bottle'],
      answer:
        '**Drinking water**\n\nPrefer **sealed bottled** or filtered water. Avoid tap water if you’re not used to it.\n\nBe careful with ice and raw salads washed in unsafe water.'
    },
    {
      id: 'vivre-guinee',
      category: 'logement',
      keywords: ['living in guinea', 'move to guinea', 'expat', 'settle', 'relocate'],
      answer:
        '**Living in Guinea**\n\nOpen **Living in Guinea** in the app: culture, languages, health, housing, procedures, useful numbers, official links (government, e-Visa, e-Tax, APIP).\n\nFor housing: compare Kaloum (expensive), Dixinn, Ratoma, Matoto; visit several places and check water/power.'
    },
    {
      id: 'ecosystem-overview',
      category: 'application',
      keywords: ['ecosystem', 'wallet', 'digital services', 'cep wallet', 'services'],
      answer:
        '**Conakry en Poche ecosystem**\n\nIn the app → **Ecosystem**:\n• **Wallet** — GNF balance, Orange Money / MTN MoMo top-up\n• **Collect (QR)** — merchant payments\n• **Doctor appointment** — consult + wallet pay\n• **Cinema / Tourism** — digital tickets\n\nThe diaspora can also **top up a card remotely** (shareable link + Stripe card).'
    },
    {
      id: 'commercial-agent',
      category: 'application',
      keywords: [
        'commercial agent',
        'become agent',
        'commission',
        '2%',
        'own boss',
        'be your own boss',
        'opportunity',
        'earn money',
        'affiliate'
      ],
      answer:
        '**Opportunities on Conakry en Poche**\n\n• **Commercial agent** — Home / Ecosystem → **Become an agent**. Share your `/a/CEP-A-…` link, earn **2%** on platform fees (business, rental, activity). **Mobile Money** payouts. Agreement: `/terms-agent`.\n• **Be your own boss** — Register a **business**, **rental agency**, or publish an **activity**. No commission on your sales.\n\nEverything is online — no meeting with app management.'
    },
    {
      id: 'wallet-topup',
      category: 'application',
      keywords: ['top up wallet', 'topup', 'recharge wallet', 'wallet balance', 'orange money wallet', 'mtn wallet'],
      answer:
        '**Top up your wallet**\n\n1. Ecosystem → **Wallet**\n2. Choose an amount and **Orange Money** or **MTN MoMo**\n3. Confirm on your phone\n\nIf merchant keys are not configured, **demo mode** lets you confirm manually. Credit then appears on your CEP card.'
    },
    {
      id: 'diaspora-recharge',
      category: 'application',
      keywords: ['diaspora', 'remote top up', 'remote top-up', 'abroad', 'stripe', 'international card', 'bank card', 'receipt', 'pdf'],
      answer:
        '**Diaspora top-up**\n\n1. Card holder opens **Wallet** → **Share diaspora link**\n2. Payer opens `/recharger/CEP-…` (no login)\n3. Chooses Mobile Money or **international card (Stripe)**\n4. Beneficiary is credited; a **PDF / email receipt** is available\n\nThe beneficiary’s full name shows on the payment page.'
    },
    {
      id: 'merchant-qr',
      category: 'application',
      keywords: ['collect', 'merchant qr', 'qr payment', 'checkout qr', 'sms notification', 'email notification', 'pay merchant'],
      answer:
        '**Merchant QR checkout**\n\n• Merchant: Ecosystem → **Collect (QR)** (or business dashboard)\n• Show the QR (optional amount)\n• Customer scans → pays with **CEP wallet**\n• You get **email and SMS** notifications\n\nFunds credit the merchant wallet immediately.'
    },
    {
      id: 'hotel-booking',
      category: 'hotels',
      keywords: ['book hotel', 'hotel booking', 'reserve hotel', 'check-in', 'check out', 'room', 'rooms'],
      answer:
        '**Book a hotel**\n\n1. **Hotels** category\n2. Open a property → **Book**\n3. Enter **check-in**, **check-out** and **rooms**\n4. Confirm (login required)\n\nTrack or cancel under **My reservations** (`/bookings`). The hotel gets a notification.'
    },
    {
      id: 'health-rdv',
      category: 'sante',
      keywords: ['doctor appointment', 'book doctor', 'consultation', 'health ecosystem', 'medical appointment'],
      answer:
        '**Doctor appointments in the ecosystem**\n\nEcosystem → **Health**: pick a slot, confirm and **pay with the Conakry en Poche wallet**.\n\nTop up first if needed. Emergencies remain **119**.'
    },
    {
      id: 'tickets-cinema-tourisme',
      category: 'loisirs',
      keywords: ['cinema ticket', 'movie ticket', 'tourism ticket', 'attraction ticket', 'tourist site'],
      answer:
        '**Cinema & tourism tickets**\n\n• Ecosystem → **Cinema**: showtimes and digital ticket\n• Ecosystem → **Tourism**: site entries\n\nPay with the **CEP wallet**. The QR ticket lives in the wallet; you can share or print it.'
    },
  ]
};
